import type { PublishedPage } from "./types";
import { shareImage } from "./publicPages.ts";
import { parseFaq } from "./compareBlocks.ts";
import { aiNewsCategory } from "./categories.ts";
import { dmitryDubovetzky } from "./authors.ts";

export function contentStructuredData(page: PublishedPage, url: string) {
  const image = new URL(shareImage(page), url).href;
  if ("kind" in page) {
    const origin = new URL(url).origin;
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${origin}/#organization`,
          name: "MaitoAI, LLC",
          url: `${origin}/`,
          email: "support@maito.app",
        },
        {
          "@type": page.path === "/about" ? "AboutPage" : "WebPage",
          name: page.title,
          description: page.description,
          url,
          image,
          publisher: { "@id": `${origin}/#organization` },
          ...("updated" in page && page.updated ? { dateModified: page.updated } : {}),
        },
        ...(page.structuredData?.(origin) ?? []),
        ...(page.path === "/homepage"
          ? [
              {
                "@type": "SoftwareApplication",
                name: "Maito",
                url,
                description: page.description,
                applicationCategory: "BusinessApplication",
                operatingSystem: "Web",
                publisher: { "@id": `${origin}/#organization` },
                offers: {
                  "@type": "Offer",
                  price: "149",
                  priceCurrency: "USD",
                  url: `${origin}/pricing`,
                  description: "Per workspace per month; AI provider usage billed separately.",
                },
              },
            ]
          : []),
      ],
    };
  }
  if (!page.date)
    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: page.title,
      description: page.description,
      url,
    };
  const article = {
    "@context": "https://schema.org",
    "@type": page.section === "compare" ? "Article" : "BlogPosting",
    headline: page.title,
    description: page.description,
    datePublished: page.date,
    dateModified: page.updated ?? page.date,
    author:
      page.author === dmitryDubovetzky.name
        ? {
            "@type": "Person",
            name: dmitryDubovetzky.name,
            image: new URL(dmitryDubovetzky.image, url).href,
          }
        : { "@type": "Organization", name: page.author ?? "Maito team" },
    publisher: { "@type": "Organization", name: "Maito" },
    ...(page.category === aiNewsCategory.id ? { articleSection: aiNewsCategory.label } : {}),
    mainEntityOfPage: url,
    ...(image ? { image } : {}),
  };
  const faq = page.markdown ? parseFaq(page.markdown) : [];
  if (!faq.length) return article;
  const { "@context": context, ...articleNode } = article;
  return {
    "@context": context,
    "@graph": [
      articleNode,
      {
        "@type": "FAQPage",
        url,
        mainEntity: faq.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      },
    ],
  };
}
